/**
 * Client Portal — Upload Assets page.
 * Clients can upload documents to support their assessment engagements.
 * Uploads are stored with source='client' and visible to assigned asset managers in the
 * Asset Management Portal's Client Uploaded Documents sub-page.
 */
import React, { useEffect, useState } from 'react';
import {
  Typography, Box, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, CircularProgress, Alert, Button, Dialog,
  DialogTitle, DialogContent, DialogActions, TextField,
} from '@mui/material';
import { Upload, CheckCircle, HourglassEmpty, Cancel } from '@mui/icons-material';
import { authFetch } from '../../../api/authFetch';
import RefreshButton from '../../../components/RefreshButton';
import PortalTooltip from '../../../components/PortalTooltip';

interface ClientUploadItem {
  id: string;
  assessment_type: string;
  asset_class: string;
  filename: string;
  description: string | null;
  status: string;
  review_notes: string | null;
  created_at: string | null;
}

const STATUS_ICONS: Record<string, React.ReactNode> = {
  pending: <HourglassEmpty fontSize="small" />,
  approved: <CheckCircle fontSize="small" />,
  rejected: <Cancel fontSize="small" />,
};

const STATUS_COLORS: Record<string, 'warning' | 'success' | 'error'> = {
  pending: 'warning',
  approved: 'success',
  rejected: 'error',
};

const ClientAssetUpload: React.FC = () => {
  const [assets, setAssets] = useState<ClientUploadItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [lastRefreshed, setLastRefreshed] = useState<Date | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    assessment_type: '',
    description: '',
  });
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const fetchUploads = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await authFetch('/api/client/assets/uploads');
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || 'Failed to load uploaded documents');
      setAssets(data.assets);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load uploaded documents');
    } finally {
      setLoading(false);
      setLastRefreshed(new Date());
    }
  };

  useEffect(() => { fetchUploads(); }, []);

  const handleSubmit = async () => {
    if (!formData.assessment_type || !selectedFile) {
      setError('Assessment type and file are required');
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      const body = new FormData();
      body.append('file', selectedFile);
      body.append('assessment_type', formData.assessment_type);
      body.append('description', formData.description);

      const res = await authFetch('/api/client/assets/upload', {
        method: 'POST',
        body,
      });
      if (!res.ok) {
        const errText = await res.text();
        let errMsg = `Upload failed (HTTP ${res.status})`;
        try {
          const errData = JSON.parse(errText);
          if (errData?.error) errMsg = errData.error;
        } catch { /* response wasn't JSON */ }
        setError(errMsg);
        return;
      }
      const data = await res.json();
      if (data.success) {
        setSuccess('Document stored successfully and available to your assigned KPMG team.');
        setDialogOpen(false);
        setFormData({ assessment_type: '', description: '' });
        setSelectedFile(null);
        fetchUploads();
        setTimeout(() => setSuccess(null), 5000);
      } else {
        setError(data.error || 'Upload failed');
      }
    } catch {
      setError('Failed to upload document');
    } finally {
      setSubmitting(false);
    }
  };

  const resetDialog = () => {
    if (submitting) return;
    setDialogOpen(false);
    setSelectedFile(null);
    setFormData({ assessment_type: '', description: '' });
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <PortalTooltip tooltipKey="client.page.upload_documents" showIcon>
            <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
              Upload Documents
            </Typography>
          </PortalTooltip>
          <Typography variant="body2" color="text.secondary">
            Share existing bid documents and supporting evidence with your assigned KPMG team.
            Documents support discovery and assessment; they do not replace the assessment questions.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <RefreshButton onRefresh={fetchUploads} loading={loading} lastRefreshed={lastRefreshed} />
          <Button variant="contained" startIcon={<Upload />} onClick={() => setDialogOpen(true)}>
            Upload Document
          </Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>{success}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
      ) : assets.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">
            You haven&apos;t uploaded any documents yet. Click &quot;Upload Document&quot; to share files with your KPMG team.
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>File Name</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Assessment Type</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Notes</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {assets.map((asset) => (
                <TableRow key={asset.id}>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>{asset.filename}</Typography>
                    {asset.description && (
                      <Typography variant="caption" color="text.secondary">{asset.description}</Typography>
                    )}
                  </TableCell>
                  <TableCell>{asset.assessment_type}</TableCell>
                  <TableCell>
                    {asset.created_at ? new Date(asset.created_at).toLocaleDateString() : '—'}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={asset.status}
                      size="small"
                      color={STATUS_COLORS[asset.status] || 'default'}
                      icon={STATUS_ICONS[asset.status] as React.ReactElement || undefined}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" color="text.secondary">
                      {asset.review_notes || '—'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Upload Dialog */}
      <Dialog open={dialogOpen} onClose={resetDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Upload Document</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Upload a document related to your assessment engagement. Your KPMG team will be able to view and download it.
            Supported: PDF, DOCX, PPTX, XLSX, TXT, CSV, JSON, PNG and JPEG, up to 25 MB per file.
          </Typography>
          <TextField
            fullWidth
            label="Assessment Type"
            placeholder="e.g. M365 Copilot Readiness"
            value={formData.assessment_type}
            onChange={(e) => setFormData({ ...formData, assessment_type: e.target.value })}
            sx={{ mb: 2 }}
            required
          />
          <Button
            component="label"
            variant="outlined"
            fullWidth
            startIcon={<Upload />}
            sx={{ mb: 2, justifyContent: 'flex-start', textTransform: 'none' }}
          >
            {selectedFile ? selectedFile.name : 'Choose File *'}
            <input
              type="file"
              accept=".pdf,.docx,.pptx,.xlsx,.txt,.csv,.json,.png,.jpg,.jpeg"
              hidden
              onChange={(e) => setSelectedFile(e.target.files?.[0] || null)}
            />
          </Button>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Description (optional)"
            placeholder="Describe what this document contains and how it relates to your assessment"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={resetDialog} disabled={submitting}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={submitting}
            startIcon={submitting ? <CircularProgress size={16} /> : <Upload />}
          >
            Upload
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ClientAssetUpload;
